// src/ui/screens/post/widgets/CommentSection.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../hooks/useAuth';
import { UserAvatar } from '../../../../components/UserAvatar';
import { db } from '../../../../firebase';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  onSnapshot,
  updateDoc,
  increment,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { notificationService } from '../../../../services/notificationService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
  grey: '#7A7A7A',
  pink: '#FF1493',
  blue: '#4DA3FF',
};

// ── Comment Model ─────────────────────────────────────────────────────────────

class CommentModel {
  constructor({
    id,
    uid,
    text,
    createdAt = null,
    likes = 0,
    likedBy = [],
    replyCount = 0,
    replyIds = [],
    isReply = false,
    parentId = null,
    isOptimistic = false,
    authorName = '',
    authorAvatar = '',
    authorVerified = false,
  }) {
    this.id = id;
    this.uid = uid;
    this.text = text;
    this.createdAt = createdAt;
    this.likes = likes;
    this.likedBy = likedBy;
    this.replyCount = replyCount;
    this.replyIds = replyIds;
    this.isReply = isReply;
    this.parentId = parentId;
    this.isOptimistic = isOptimistic;
    this.authorName = authorName;
    this.authorAvatar = authorAvatar;
    this.authorVerified = authorVerified;
  }

  static fromDoc(doc) {
    const data = doc.data();
    return new CommentModel({
      id: doc.id,
      uid: data.uid || '',
      text: data.text || '',
      createdAt: data.createdAt?.toDate?.() || null,
      likes: data.likes || 0,
      likedBy: data.likedBy || [],
      replyCount: data.replyCount || 0,
      replyIds: data.replyIds || [],
      isReply: data.isReply || false,
      parentId: data.parentId || null,
    });
  }
}

// ── Time formatter ───────────────────────────────────────────────────────────

const formatTime = (dt) => {
  if (!dt) return 'Just now';
  const diff = Date.now() - dt.getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};

// ── CommentSection Component ─────────────────────────────────────────────────

export const CommentSection = ({ listingId, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [listingTitle, setListingTitle] = useState('');
  const [listingOwnerId, setListingOwnerId] = useState('');
  const [listingThumbnailUrl, setListingThumbnailUrl] = useState('');
  const [listingImageUrls, setListingImageUrls] = useState([]);
  const [currentUserAvatar, setCurrentUserAvatar] = useState('');
  const [currentUserName, setCurrentUserName] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [inputText, setInputText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);

  const scrollContainerRef = useRef(null);
  const inputRef = useRef(null);

  // ── Fetch listing details ─────────────────────────────────────────────────

  useEffect(() => {
    const fetchListingDetails = async () => {
      try {
        const snap = await getDoc(doc(db, 'listings', listingId));
        if (snap.exists()) {
          const data = snap.data();
          setListingTitle(data.title || '');
          setListingOwnerId(data.uid || '');
          setListingThumbnailUrl(data.thumbnailUrl || '');
          setListingImageUrls(data.imageUrls || []);
        }
      } catch (error) {
        console.error('CommentSection fetchDetails error:', error);
      }
    };
    fetchListingDetails();
  }, [listingId]);

  // ── Subscribe to current user ─────────────────────────────────────────────

  useEffect(() => {
    if (!user) return;

    const unsubscribe = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setCurrentUserAvatar(data.profileImage || data.photoURL || '');
        setCurrentUserName(data.displayName || data.name || user.displayName || '');
      }
    }, (error) => {
      console.error('CommentSection currentUser error:', error);
    });

    return () => unsubscribe();
  }, [user]);

  // ── Subscribe to comments ─────────────────────────────────────────────────

  useEffect(() => {
    const commentsRef = collection(db, 'listings', listingId, 'comments');
    const q = query(commentsRef, where('isReply', '==', false), orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const incoming = snapshot.docs.map(doc => CommentModel.fromDoc(doc));

      setComments(prev => {
        // Preserve optimistic comments
        const optimistic = prev.filter(c => c.id.startsWith('_opt_'));
        const merged = [...incoming];

        for (const opt of optimistic) {
          const confirmed = incoming.some(c =>
            c.uid === opt.uid &&
            c.text === opt.text &&
            (!c.createdAt || Math.abs(Date.now() - c.createdAt.getTime()) < 10000)
          );
          if (!confirmed) merged.push(opt);
        }

        setIsLoading(false);
        return merged;
      });
    }, (error) => {
      console.error('CommentSection stream error:', error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [listingId]);

  // ── Scroll to bottom ──────────────────────────────────────────────────────

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    if (shouldScrollToBottom) {
      scrollToBottom();
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom, scrollToBottom]);

  // ── Submit comment ────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const text = inputText.trim();
    if (!text || !user || isSending) return;

    const replyTarget = replyingTo;

    const optimisticId = `_opt_${Date.now()}`;
    const optimistic = new CommentModel({
      id: optimisticId,
      uid: user.uid,
      text: text,
      createdAt: new Date(),
      likes: 0,
      likedBy: [],
      replyCount: 0,
      replyIds: [],
      isReply: !!replyTarget,
      parentId: replyTarget?.id || null,
      isOptimistic: true,
      authorName: currentUserName || user.displayName || 'You',
      authorAvatar: currentUserAvatar || '',
      authorVerified: false,
    });

    setComments(prev => {
      if (!replyTarget) {
        setShouldScrollToBottom(true);
        return [...prev, optimistic];
      }
      return prev;
    });
    setInputText('');
    setReplyingTo(null);
    setIsSending(true);

    try {
      const commentsRef = collection(db, 'listings', listingId, 'comments');

      const data = {
        uid: user.uid,
        text: text,
        createdAt: serverTimestamp(),
        likes: 0,
        likedBy: [],
        replyCount: 0,
        replyIds: [],
        isReply: !!replyTarget,
        parentId: replyTarget?.id || null,
      };

      const docRef = await addDoc(commentsRef, data);

      if (replyTarget) {
        await updateDoc(doc(commentsRef, replyTarget.id), {
          replyCount: increment(1),
          replyIds: arrayUnion(docRef.id),
        });
      }

      // Update listing comment count
      await updateDoc(doc(db, 'listings', listingId), {
        commentCount: increment(1),
      }).catch(() => {});

      // Send comment notification
      if (listingOwnerId && listingOwnerId !== user.uid) {
        const thumbnailUrl = listingThumbnailUrl || listingImageUrls[0] || null;
        await notificationService.onComment({
          listingOwnerId,
          listingId,
          listingTitle: listingTitle || 'your listing',
          commentText: text,
          thumbnailUrl,
          listingImageUrl: listingImageUrls[0] || null,
        });
      }
    } catch (error) {
      console.error('CommentSection submit error:', error);
      setComments(prev => prev.filter(c => c.id !== optimisticId));
    } finally {
      setIsSending(false);
    }
  };

  // ── Render comment item ───────────────────────────────────────────────────

  const renderCommentItem = (comment, index) => {
    const isOwnComment = comment.uid === user?.uid;

    return (
      <CommentItem
        key={comment.id}
        comment={comment}
        listingId={listingId}
        currentUserId={user?.uid || ''}
        isOwnComment={isOwnComment}
        onReply={(c) => {
          setReplyingTo(c);
          inputRef.current?.focus();
        }}
        onOpenProfile={(uid) => navigate(`/profile/${uid}`)}
        isLast={index === comments.length - 1}
      />
    );
  };

  // ── Main render ──────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
    >
      <div
        className="w-full max-w-md bg-filatom-card rounded-t-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}
      >
        {/* Drag Handle */}
        <div className="flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-white/20" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <span className="text-white font-bold text-sm">
            {isLoading ? 'Comments' : `Comments (${comments.length})`}
          </span>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: COLORS.cardLight }}
          >
            <svg className="w-4.5 h-4.5 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Listing Title */}
        {listingTitle && (
          <div className="px-5 pb-1">
            <span className="text-white/45 text-xs font-medium truncate block">
              {listingTitle}
            </span>
          </div>
        )}

        <div className="h-px bg-white/5 mx-5 my-1" />

        {/* Comments List */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto px-4 pb-2"
          style={{ maxHeight: '50vh' }}
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32">
              <svg className="w-12 h-12 text-white/12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-white/35 text-sm mt-3">No comments yet.<br />Be the first!</span>
            </div>
          ) : (
            comments.map((comment, i) => renderCommentItem(comment, i))
          )}
        </div>

        {/* Reply Banner */}
        {replyingTo && (
          <div
            className="mx-4 mt-1 px-3.5 py-2.5 rounded-xl flex items-center justify-between"
            style={{
              backgroundColor: `${COLORS.orange}1A`,
              border: `1px solid ${COLORS.orange}40`,
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke={COLORS.orange} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="text-filatom-primary text-xs font-medium truncate">
                Replying to {replyingTo.authorName || 'Unknown'}
              </span>
            </div>
            <button
              onClick={() => setReplyingTo(null)}
              className="text-white/50 hover:text-white/80"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-2.5 flex items-end gap-2.5 border-t border-white/5">
          <UserAvatar
            size={36}
            imageUrl={currentUserAvatar || null}
            name={currentUserName}
          />
          <div className="flex-1">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              placeholder={replyingTo ? `Reply to ${replyingTo.authorName}…` : 'Write a comment…'}
              className="w-full px-4 py-2.5 rounded-full text-white text-sm outline-none border border-transparent focus:border-filatom-primary/50"
              style={{
                backgroundColor: COLORS.cardLight,
                color: '#FFFFFF',
              }}
            />
          </div>
          <button
            onClick={handleSubmit}
            disabled={!inputText.trim() || isSending}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              inputText.trim() && !isSending
                ? 'bg-filatom-primary hover:opacity-90'
                : 'bg-filatom-cardLight cursor-not-allowed'
            }`}
          >
            {isSending ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg
                className={`w-4.5 h-4.5 ${inputText.trim() ? 'text-black' : 'text-filatom-grey'}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>

        {/* Bottom padding for safe area */}
        <div className="h-2" />
      </div>
    </div>
  );
};

// ── CommentItem Component ────────────────────────────────────────────────────

const CommentItem = ({
  comment,
  listingId,
  currentUserId,
  isOwnComment,
  onReply,
  onOpenProfile,
  isLast = false,
}) => {
  const [authorName, setAuthorName] = useState(comment.authorName || '');
  const [authorAvatar, setAuthorAvatar] = useState(comment.authorAvatar || '');
  const [authorVerified, setAuthorVerified] = useState(comment.authorVerified || false);
  const [likeCount, setLikeCount] = useState(comment.likes);
  const [isLiked, setIsLiked] = useState(comment.likedBy.includes(currentUserId));
  const [isLiking, setIsLiking] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);
  const [repliesExpanded, setRepliesExpanded] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);

  // ── Fetch author data ─────────────────────────────────────────────────────

  useEffect(() => {
    if (comment.isOptimistic || comment.authorName) return;

    const unsubscribe = onSnapshot(doc(db, 'users', comment.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAuthorName(data.displayName || data.name || 'Unknown');
        setAuthorAvatar(data.profileImage || data.photoURL || '');
        setAuthorVerified(data.verified || false);
      }
    }, (error) => {
      console.error('CommentItem author error:', error);
    });

    return () => unsubscribe();
  }, [comment.uid, comment.isOptimistic, comment.authorName]);

  // ── Subscribe to replies ─────────────────────────────────────────────────

  useEffect(() => {
    if (comment.replyCount === 0) return;

    const repliesRef = collection(db, 'listings', listingId, 'comments');
    const q = query(repliesRef, where('parentId', '==', comment.id), orderBy('createdAt', 'asc'));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const replyList = snapshot.docs.map(doc => CommentModel.fromDoc(doc));
      setReplies(replyList);
      setLoadingReplies(false);
    }, (error) => {
      console.error('CommentItem replies error:', error);
      setLoadingReplies(false);
    });

    return () => unsubscribe();
  }, [comment.id, comment.replyCount, listingId]);

  // ── Handle like ──────────────────────────────────────────────────────────

  const handleLike = async () => {
    if (!currentUserId || isLiking) return;
    setIsLiking(true);

    const ref = doc(db, 'listings', listingId, 'comments', comment.id);
    try {
      if (isLiked) {
        await updateDoc(ref, {
          likes: increment(-1),
          likedBy: arrayRemove(currentUserId),
        });
        setLikeCount(prev => prev - 1);
        setIsLiked(false);
      } else {
        await updateDoc(ref, {
          likes: increment(1),
          likedBy: arrayUnion(currentUserId),
        });
        setLikeCount(prev => prev + 1);
        setIsLiked(true);
      }
    } catch (error) {
      console.error('CommentItem like error:', error);
    } finally {
      setIsLiking(false);
    }
  };

  // ── Render text with expand ─────────────────────────────────────────────

  const renderText = () => {
    const text = comment.text;
    const maxLen = 160;
    const isLong = text.length > maxLen;

    if (!isLong) {
      return <span className="text-white/90 text-sm leading-relaxed">{text}</span>;
    }

    return (
      <button
        onClick={() => setTextExpanded(!textExpanded)}
        className="text-left"
      >
        <span className="text-white/90 text-sm leading-relaxed">
          {textExpanded ? text : text.substring(0, maxLen).trim()}
          {!textExpanded && (
            <>
              … <span className="text-filatom-grey font-semibold">more</span>
            </>
          )}
          {textExpanded && (
            <span className="text-filatom-grey font-semibold"> less</span>
          )}
        </span>
      </button>
    );
  };

  // ── Render replies ──────────────────────────────────────────────────────

  const renderReplies = () => {
    if (!repliesExpanded) return null;

    return (
      <div className="ml-4 pl-3.5 border-l border-white/10 mt-2">
        {replies.map((reply) => (
          <ReplyItem
            key={reply.id}
            reply={reply}
            listingId={listingId}
            currentUserId={currentUserId}
            onReply={onReply}
            onOpenProfile={onOpenProfile}
          />
        ))}
      </div>
    );
  };

  const opacity = comment.isOptimistic ? 0.65 : 1;

  return (
    <div className={`py-3 border-b border-white/5 ${isLast ? 'border-b-0' : ''}`} style={{ opacity }}>
      <div className="flex gap-2.5">
        <button
          onClick={() => {
            if (!isOwnComment) onOpenProfile(comment.uid);
          }}
          className="flex-shrink-0"
        >
          <UserAvatar
            size={36}
            imageUrl={authorAvatar || null}
            name={authorName}
          />
        </button>

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => {
                if (!isOwnComment) onOpenProfile(comment.uid);
              }}
              className="text-left"
            >
              <span className={`text-sm font-semibold ${isOwnComment ? 'text-filatom-primary' : 'text-white'}`}>
                {authorName || '…'}
              </span>
            </button>
            {authorVerified && (
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: COLORS.blue }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            )}
            <span className="text-filatom-grey text-[11px]">{formatTime(comment.createdAt)}</span>
          </div>

          {/* Text */}
          <div className="mt-1">{renderText()}</div>

          {/* Actions */}
          <div className="flex items-center gap-4 mt-1.5">
            <button onClick={handleLike} disabled={isLiking} className="flex items-center gap-1">
              <svg
                className={`w-4 h-4 ${isLiked ? 'text-pink-500' : 'text-filatom-grey'}`}
                fill={isLiked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {likeCount > 0 && (
                <span className={`text-xs ${isLiked ? 'text-pink-500' : 'text-filatom-grey'}`}>
                  {likeCount}
                </span>
              )}
            </button>

            <button onClick={() => onReply(comment)} className="flex items-center gap-1">
              <svg className="w-4 h-4 text-filatom-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="text-filatom-grey text-xs">Reply</span>
            </button>
          </div>

          {/* Replies toggle */}
          {comment.replyCount > 0 && (
            <button
              onClick={() => setRepliesExpanded(!repliesExpanded)}
              className="flex items-center gap-1.5 mt-2"
            >
              <div className="w-5 h-px bg-filatom-grey/50" />
              <span className="text-filatom-grey text-xs font-medium">
                {repliesExpanded ? 'Hide replies' : `View ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
              </span>
              <svg
                className={`w-4 h-4 text-filatom-grey transition-transform ${repliesExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}

          {/* Replies */}
          {renderReplies()}
        </div>
      </div>
    </div>
  );
};

// ── ReplyItem Component ──────────────────────────────────────────────────────

const ReplyItem = ({ reply, listingId, currentUserId, onReply, onOpenProfile }) => {
  const [authorName, setAuthorName] = useState(reply.authorName || '');
  const [authorAvatar, setAuthorAvatar] = useState(reply.authorAvatar || '');
  const [authorVerified, setAuthorVerified] = useState(reply.authorVerified || false);
  const [likeCount, setLikeCount] = useState(reply.likes);
  const [isLiked, setIsLiked] = useState(reply.likedBy.includes(currentUserId));
  const [isLiking, setIsLiking] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);

  const isOwnReply = reply.uid === currentUserId;

  useEffect(() => {
    if (reply.isOptimistic || reply.authorName) return;

    const unsubscribe = onSnapshot(doc(db, 'users', reply.uid), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setAuthorName(data.displayName || data.name || 'Unknown');
        setAuthorAvatar(data.profileImage || data.photoURL || '');
        setAuthorVerified(data.verified || false);
      }
    });

    return () => unsubscribe();
  }, [reply.uid, reply.isOptimistic, reply.authorName]);

  const handleLike = async () => {
    if (!currentUserId || isLiking) return;
    setIsLiking(true);

    const ref = doc(db, 'listings', listingId, 'comments', reply.id);
    try {
      if (isLiked) {
        await updateDoc(ref, {
          likes: increment(-1),
          likedBy: arrayRemove(currentUserId),
        });
        setLikeCount(prev => prev - 1);
        setIsLiked(false);
      } else {
        await updateDoc(ref, {
          likes: increment(1),
          likedBy: arrayUnion(currentUserId),
        });
        setLikeCount(prev => prev + 1);
        setIsLiked(true);
      }
    } catch (error) {
      console.error('ReplyItem like error:', error);
    } finally {
      setIsLiking(false);
    }
  };

  const renderText = () => {
    const text = reply.text;
    const maxLen = 120;
    const isLong = text.length > maxLen;

    if (!isLong) {
      return <span className="text-white/85 text-sm leading-relaxed">{text}</span>;
    }

    return (
      <button onClick={() => setTextExpanded(!textExpanded)} className="text-left">
        <span className="text-white/85 text-sm leading-relaxed">
          {textExpanded ? text : text.substring(0, maxLen).trim()}
          {!textExpanded && (
            <>
              … <span className="text-filatom-grey font-semibold">more</span>
            </>
          )}
          {textExpanded && (
            <span className="text-filatom-grey font-semibold"> less</span>
          )}
        </span>
      </button>
    );
  };

  const opacity = reply.isOptimistic ? 0.65 : 1;

  return (
    <div className={`py-2.5 border-b border-white/5 last:border-b-0`} style={{ opacity }}>
      <div className="flex gap-2">
        <button onClick={() => {
          if (!isOwnReply) onOpenProfile(reply.uid);
        }} className="flex-shrink-0">
          <UserAvatar size={28} imageUrl={authorAvatar || null} name={authorName} />
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button onClick={() => {
              if (!isOwnReply) onOpenProfile(reply.uid);
            }} className="text-left">
              <span className={`text-xs font-semibold ${isOwnReply ? 'text-filatom-primary' : 'text-white'}`}>
                {authorName || '…'}
              </span>
            </button>
            {authorVerified && (
              <svg className="w-2.5 h-2.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: COLORS.blue }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            )}
            <span className="text-filatom-grey text-[10px]">{formatTime(reply.createdAt)}</span>
          </div>

          <div className="mt-0.5">{renderText()}</div>

          <div className="flex items-center gap-3 mt-1">
            <button onClick={handleLike} disabled={isLiking} className="flex items-center gap-1">
              <svg
                className={`w-3.5 h-3.5 ${isLiked ? 'text-pink-500' : 'text-filatom-grey'}`}
                fill={isLiked ? 'currentColor' : 'none'}
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth={2}
              >
                <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {likeCount > 0 && (
                <span className={`text-[11px] ${isLiked ? 'text-pink-500' : 'text-filatom-grey'}`}>
                  {likeCount}
                </span>
              )}
            </button>

            <button onClick={() => onReply(reply)} className="flex items-center gap-1">
              <svg className="w-3.5 h-3.5 text-filatom-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
              <span className="text-filatom-grey text-[11px]">Reply</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentSection;